/**
 * Button/input/link @apply property sets from src/styles/propertySets.css, as sx objects.
 * Mirrors the source verbatim. Custom-media → literal sx keys:
 * --viewportSmall '@media (min-width:600px)', --viewportMedium '@media (min-width:900px)',
 * --viewportLarge '@media (min-width:1200px)', --viewportLargeWithPaddings '@media (min-width:1304px)',
 * --viewportXLarge '@media (min-width:1921px)'.
 *
 * Sets containing @apply are expanded inline: the @apply target's declarations are inlined at the
 * point of use, with subsequent own declarations overriding conflicting ones (last-wins, per CSS).
 * Multiple @media blocks for the same breakpoint within a set are merged into a single key.
 *
 * DANGLING sets — defined nowhere in propertySets.css, render nothing today, NOT exported:
 *   marketplaceButtonStylesPrimary, marketplaceButtonStylesSecondary,
 *   marketplaceButtonStylesVansite, marketplaceButtonStylesAttention,
 *   marketplaceSecondaryButtonStylesVansite.
 *
 * @param {Record<string,string>} tokens - createTokens() result
 * @returns {Record<string, object>}
 */
export const createButtonInputStyleMixins = (tokens) => ({
  // No declarations in the @media block (only a TODO comment) — media key omitted.
  marketplaceButtonFontStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightSemiBold,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.2px',
  },

  // @apply --marketplaceButtonFontStyles expanded inline (first 5 declarations).
  // Two separate @media (--viewportMedium) blocks merged: first sets padding 20px 0,
  // second sets min-height 65px → single media key with both declarations.
  marketplaceButtonStyles: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightSemiBold,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.2px',
    display: 'flex',
    width: '100%',
    minHeight: '59px',
    margin: '0',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '17px 0 17px 0',
    border: 'none',
    borderRadius: tokens.borderRadius,
    backgroundColor: tokens.marketplaceColor,
    color: tokens.matterColorLight,
    textAlign: 'center',
    textDecoration: 'none',
    transition: `all ${tokens.transitionStyleButton}`,
    cursor: 'pointer',
    '&:hover, &:focus': {
      outline: 'none',
      backgroundColor: tokens.marketplaceColorDark,
      textDecoration: 'none',
      boxShadow: tokens.boxShadowButton,
    },
    '&:disabled': {
      backgroundColor: tokens.matterColorNegative,
      color: tokens.matterColorLight,
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
    '@media (min-width:900px)': {
      padding: '20px 0 20px 0',
      minHeight: '65px',
    },
  },

  marketplaceInputStyles: {
    display: 'block',
    width: '100%',
    margin: '0',
    padding: '5px 0 5px 0',
    border: 'none',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.marketplaceColor,
    borderRadius: '0',
    '&::placeholder': {
      color: tokens.matterColorAnti,
    },
    transition: `border-bottom-color ${tokens.transitionStyle}`,
    '&:hover, &:focus': {
      borderBottomColor: tokens.matterColor,
      outline: 'none',
    },
    '@media (min-width:900px)': {
      padding: '4px 0 2px 0',
    },
  },

  marketplaceSelectStyles: {
    display: 'block',
    width: '100%',
    margin: '0',
    padding: '5px 0 5px 0',
    appearance: 'none',
    border: 'none',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.marketplaceColor,
    borderRadius: '0',
    backgroundImage:
      "url('data:image/svg+xml;utf8,<svg width=\"14\" height=\"9\" viewBox=\"0 0 14 9\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M6.53 7.472c.26.26.68.26.94 0l5.335-5.333c.26-.263.26-.684 0-.944-.26-.26-.683-.26-.943 0L7 6.056l-4.862-4.86c-.26-.26-.683-.26-.943 0-.26.26-.26.68 0 .943L6.53 7.47z\" stroke=\"%234A4A4A\" fill=\"%234A4A4A\" fill-rule=\"evenodd\"/></svg>')",
    backgroundSize: '16px 16px',
    backgroundPosition: 'center right',
    cursor: 'pointer',
    transition: `border-bottom-color ${tokens.transitionStyle}`,
    '&:hover, &:focus': {
      borderBottomColor: tokens.matterColor,
      outline: 'none',
    },
    '@media (min-width:900px)': {
      padding: '4px 0 2px 0',
    },
  },

  marketplaceLinkStyles: {
    display: 'inline',
    margin: '0',
    padding: '0',
    color: tokens.marketplaceColor,
    textDecoration: 'none',
    fontWeight: '600',
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },

  marketplaceLinkStylesLight: {
    display: 'inline',
    margin: '0',
    padding: '0',
    border: 'none',
    color: tokens.marketplaceColorLight,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
});
